'use client';

import { memo, useRef, useState } from 'react';
import { Handle, NodeToolbar, Position, useStore } from '@xyflow/react';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { HeadingItem, MetaDefault } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { CrownIcon, Cancel01Icon, type TerraIcon } from '@terra/ui-icons';
import type { AttackPathEventNodeData } from './attack-path-event-node.types';
import { NODE_WIDTH } from './assessment-attack-graph-layout.constants';
import { InternalNetworkAttackPathEventPopover } from './InternalNetworkAttackPathEventPopover';
import { resolveAttackPathToolbarPosition } from './attack-path-node-toolbar-position.util';
import { useAttackPathCampaignHover } from './attack-path-campaign-hover-context';
import { useAttackPathPopoverLayer } from './attack-path-popover-layer-context';
import { AttackPathNodePagePopover } from './AttackPathNodePagePopover';
import { AttackPathCampaignFrameNode } from './InternalNetworkAttackPathCampaignFrameNode';
import { AttackPathEventEdge } from './InternalNetworkAttackPathEventEdge';
import {
  ATTACK_PATH_FAILED_EVENT_COLOR,
  isFailedAttackPathEventStatus,
  resolveAttackPathEventIcon,
  resolveAttackPathEventNodeColor,
  resolveAttackPathEventNodeFrameColor,
  translucentColor,
} from './attack-path-event-visuals';

const SELECTED_RING = `0 0 0 3px ${token('colors.border.accent.focus')}`;
const ICON_TILE_SIZE = 32;
const ICON_INNER_SIZE = 16;

type NodeCornerBadgeProps = {
  icon: TerraIcon;
  backgroundColor: string;
};

const NodeCornerBadge = ({
  icon: Icon,
  backgroundColor,
}: NodeCornerBadgeProps) => (
  <Box
    position="absolute"
    top="-6px"
    right="-6px"
    w="18px"
    h="18px"
    borderRadius="full"
    display="flex"
    alignItems="center"
    justifyContent="center"
    borderWidth="1px"
    borderColor="bg.default"
    style={{ backgroundColor }}
  >
    <Icon size={10} color="white" strokeWidth={2.5} />
  </Box>
);

type AttackPathEventIconTileProps = {
  icon: TerraIcon;
  color: string;
  isUnknownGap: boolean;
  animation?: string;
};

const AttackPathEventIconTile = ({
  icon: Icon,
  color,
  isUnknownGap,
  animation,
}: AttackPathEventIconTileProps) => (
  <Box
    flexShrink={0}
    w={`${ICON_TILE_SIZE}px`}
    h={`${ICON_TILE_SIZE}px`}
    borderRadius="md"
    borderWidth="1px"
    borderStyle="solid"
    display="flex"
    alignItems="center"
    justifyContent="center"
    style={
      isUnknownGap
        ? { backgroundColor: color, borderColor: token('colors.border.subtle') }
        : {
            backgroundColor: translucentColor({ color, percent: 16 }),
            borderColor: translucentColor({ color, percent: 45 }),
            color,
            animation,
          }
    }
  >
    {isUnknownGap ? (
      <HeadingItem color="fg.muted">?</HeadingItem>
    ) : (
      <Icon size={ICON_INNER_SIZE} strokeWidth={2} />
    )}
  </Box>
);

type AttackPathEventNodeProps = {
  id?: string;
  data: AttackPathEventNodeData;
  selected?: boolean;
};

export const AttackPathEventNode = memo(
  ({ id, data, selected }: AttackPathEventNodeProps) => {
    const tEntityKinds = useTranslations(
      'internalNetwork.crownJewels.entityKinds',
    );
    const tEventKinds = useTranslations('internalNetwork.attackPathEventKinds');
    const [isHovered, setIsHovered] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState<Position>(
      Position.Top,
    );
    const nodeRef = useRef<HTMLDivElement>(null);
    const flowDomNode = useStore((state) => state.domNode);
    const campaignHover = useAttackPathCampaignHover();
    const popoverLayer = useAttackPathPopoverLayer();
    const hoverDimmed =
      campaignHover.campaignId != null &&
      (id == null || !(campaignHover.brightNodeIds?.has(id) ?? false));
    const isDimmed = Boolean(data.dimmed || hoverDimmed);
    const Icon = resolveAttackPathEventIcon({
      kind: data.kind,
      provenance: data.provenance,
      resourceKind: data.resourceKind,
      isDomainController: data.isDomainController,
      role: data.role,
      entityKind: data.entityKind,
    });
    const nodeColor = resolveAttackPathEventNodeColor(data);
    const destinationPull = data.destinationPull ?? 'idle';
    const isSeedNode =
      data.role === 'OWNED_SEED' ||
      data.role === 'CROWN_JEWEL_SEED' ||
      data.role === 'ENTITY';
    const supportingLine =
      data.role === 'ENTITY' && data.subtitle
        ? data.subtitle
        : isSeedNode && data.entityKind && data.typeLabel !== 'GOAL'
          ? tEntityKinds(data.entityKind)
          : data.subtitle ||
            (data.role === 'EVENT' ? tEventKinds(data.kind) : null);
    const isFailedEvent = isFailedAttackPathEventStatus(data.status);
    const showPopover =
      isHovered &&
      !data.hidden &&
      !hoverDimmed &&
      data.role !== 'UNKNOWN_GAP' &&
      data.role !== 'CROWN_JEWEL_CLUSTER';
    const showFailedBadge = isFailedEvent && data.role === 'EVENT';
    const isEmphasized = Boolean(
      selected || data.highlighted || data.spotlightActive,
    );
    const cardBorderColor = resolveAttackPathEventNodeFrameColor({
      data,
      emphasized: isEmphasized,
    });
    const cardShadow = isEmphasized ? SELECTED_RING : undefined;
    const iconAnimation = data.isCampaignRunning
      ? 'attack-path-node-pulse 1.8s ease-in-out infinite'
      : destinationPull === 'near'
        ? 'attack-path-destination-near 1.4s ease-in-out infinite'
        : undefined;
    const visibleOpacity = data.hidden
      ? 0
      : isDimmed
        ? data.role === 'OWNED_SEED'
          ? 0.4
          : 0.22
        : 1;
    const scale = data.hidden
      ? 0.92
      : data.climaxActive || destinationPull === 'locked'
        ? 1.06
        : data.spotlightActive
          ? 1.03
          : 1;
    const isCrownJewelMarked =
      (data.isCrownJewel || data.role === 'CROWN_JEWEL_SEED') &&
      data.role !== 'CROWN_JEWEL_CLUSTER';

    return (
      <Box
        ref={nodeRef}
        position="relative"
        opacity={visibleOpacity}
        w={`${NODE_WIDTH}px`}
        onMouseEnter={() => {
          setPopoverPosition(
            resolveAttackPathToolbarPosition({
              nodeBounds: nodeRef.current?.getBoundingClientRect() ?? null,
              containerBounds: flowDomNode?.getBoundingClientRect() ?? null,
            }),
          );
          setIsHovered(true);
        }}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          transform: `scale(${scale})`,
          transition:
            'opacity 0.4s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)',
          filter: data.climaxActive
            ? `drop-shadow(0 0 12px ${nodeColor})`
            : undefined,
          pointerEvents: data.hidden ? 'none' : undefined,
        }}
      >
        <Handle
          type="target"
          position={Position.Left}
          style={{ opacity: 0, visibility: 'hidden' }}
        />
        <Handle
          type="source"
          position={Position.Right}
          style={{ opacity: 0, visibility: 'hidden' }}
        />
        {showPopover && popoverLayer === 'page' ? (
          <AttackPathNodePagePopover anchorRef={nodeRef}>
            <InternalNetworkAttackPathEventPopover data={data} />
          </AttackPathNodePagePopover>
        ) : showPopover ? (
          <NodeToolbar isVisible position={popoverPosition} offset={12}>
            <InternalNetworkAttackPathEventPopover data={data} />
          </NodeToolbar>
        ) : null}
        {data.climaxActive ? (
          <Box
            position="absolute"
            inset="-6px"
            borderRadius="xl"
            borderWidth="2px"
            borderColor="orange.9"
            style={{
              animation: 'attack-path-node-climax 1.1s ease-out forwards',
              pointerEvents: 'none',
            }}
          />
        ) : null}
        <Box
          bg="bg.default"
          borderWidth="1px"
          borderStyle="solid"
          borderRadius="lg"
          px="3"
          py="2"
          w="full"
          cursor="pointer"
          transition="border-color 0.15s ease, box-shadow 0.15s ease"
          _hover={
            isDimmed || data.hidden
              ? undefined
              : { bg: 'bg.neutral.subtleSoft' }
          }
          style={{ borderColor: cardBorderColor, boxShadow: cardShadow }}
        >
          <HStack gap="2" alignItems="center" w="full" minW={0}>
            <AttackPathEventIconTile
              icon={Icon}
              color={nodeColor}
              isUnknownGap={data.role === 'UNKNOWN_GAP'}
              animation={iconAnimation}
            />
            <VStack
              gap="0"
              alignItems="flex-start"
              minW={0}
              flex="1"
              overflow="hidden"
            >
              <HeadingItem
                title={data.title}
                w="full"
                minW={0}
                truncate
                lineClamp={1}
              >
                {data.title}
              </HeadingItem>
              {supportingLine ? (
                <MetaDefault
                  title={supportingLine}
                  color="fg.muted"
                  w="full"
                  minW={0}
                  truncate
                >
                  {supportingLine}
                </MetaDefault>
              ) : null}
            </VStack>
          </HStack>
        </Box>
        {isCrownJewelMarked ? (
          <NodeCornerBadge
            icon={CrownIcon}
            backgroundColor={token('colors.orange.9')}
          />
        ) : showFailedBadge ? (
          <NodeCornerBadge
            icon={Cancel01Icon}
            backgroundColor={ATTACK_PATH_FAILED_EVENT_COLOR}
          />
        ) : null}
      </Box>
    );
  },
);
AttackPathEventNode.displayName = 'AttackPathEventNode';

export { AttackPathEventEdge };

export const attackPathEventNodeTypes = {
  attackPathEventNode: AttackPathEventNode,
  campaignFrame: AttackPathCampaignFrameNode,
  signalExecution: AttackPathCampaignFrameNode,
};
export const attackPathEventEdgeTypes = {
  attackPathEventEdge: AttackPathEventEdge,
};
